import { Icon, SideBar } from "pepsico-ds";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useAppConfig } from "../../context/AppConfigContext";
import { GlobalConfigContext } from "../../context/GlobalConfigContext";
import { useHasAccessPermission } from "../../hooks/useHasAccessPermission";
import { FeatureToggle, useFeatureToggle } from "../../utils/featureToggle";
import "./sidebar.scss";

const NavItem = ({ title, navigateTo }) => (
  <Link
    to={navigateTo}
    style={{
      height: "100%",
      textDecoration: "none",
      whiteSpace: "nowrap",
      width: "100%",
    }}
  >
    {title}
  </Link>
);

const CustomSideBar = () => {
  const { t } = useTranslation();
  const {
    globalState: { isPanelOpen },
    updateSidebarPanelState,
  } = useContext(GlobalConfigContext);
  const { appConfig } = useAppConfig();
  const hasAccess = useHasAccessPermission();

  // Get feature toggle states
  const featureToggles = {
    showPerformances: useFeatureToggle(FeatureToggle.showPerformancesSideMenu),
    showBonus: useFeatureToggle(FeatureToggle.isBonusFlowEnabled),
    showLeaderboards: useFeatureToggle(FeatureToggle.showLeaderboards),
    showContentManagment: useFeatureToggle(
      FeatureToggle.showContentManagmentSideMenu
    ),
    showCodeGroupSideMenu: useFeatureToggle(FeatureToggle.showCodeGroupSideMenu),
    showRewardsCatalog: useFeatureToggle(FeatureToggle.displayRewardsCatalogs),
  };

  // Filter navigation based on feature toggles
  const navigationConfig = (appConfig?.navigation || []).reduce((acc, menuItem) => {
    const { key } = menuItem;

    // Handle specific menu items based on feature toggles
    switch (key) {
      case "performances":
        if (!featureToggles.showPerformances) return acc;
        break;

      case "campaign_list": {
        // Filter children based on feature toggles
        let filteredChildren = menuItem.children || [];

        if (!featureToggles.showBonus) {
          filteredChildren = filteredChildren.filter(
            (child) => child.title !== "Bonus List"
          );
        }

        if (!featureToggles.showLeaderboards) {
          filteredChildren = filteredChildren.filter(
            (child) =>
              !["Leaderboards", "Archived Leaderboards"].includes(child.title)
          );
        }

        acc.push({ ...menuItem, children: filteredChildren });
        return acc;
      }

      case "content_list": {
        // Filter content management if toggle is off
        const contentChildren = featureToggles.showContentManagment
          ? menuItem.children
          : (menuItem.children || []).filter(
              (child) => child.id !== "content_management"
            );

        acc.push({ ...menuItem, children: contentChildren });
        return acc;
      }

      case "code_groups_keywords":
        if (!featureToggles.showCodeGroupSideMenu) return acc;
        break;

      case "rewards_product_keywords":
        // Handle rewards/catalogs visibility
        if (!featureToggles.showRewardsCatalog) {
          const rewardsChildren = (menuItem.children || []).filter(
            (child) => child.title !== "Rewards/Catalogs"
          );
          acc.push({ ...menuItem, children: rewardsChildren });
          return acc;
        }
        break;
    }

    // Default: include the menu item
    acc.push(menuItem);
    return acc;
  }, []);

  // Filter by permissions and visibility
  const accessibleNavigation = navigationConfig
    .filter((item) => hasAccess(`navigation.${item?.key}`) && !item?.hideFromNav)
    .map((item) => {
      // Map sublinks (children)
      const sublinks = item.children?.map((child) => ({
        label: (
          <NavItem
            key={`nav-${child.navigateTo}`}
            title={child.i18nKey ? t(child.i18nKey) : child.title}
            navigateTo={child.navigateTo}
          />
        ),
      }));

      const title = item.i18nKey ? t(item.i18nKey) : item.title;

      return {
        label: sublinks?.length ? (
          title
        ) : (
          <NavItem
            key={`nav-${item.navigateTo}`}
            title={title}
            navigateTo={item.navigateTo}
          />
        ),
        icon: item.icon,
        url: item.navigateTo,
        sublinks: sublinks?.length ? sublinks : undefined,
      };
    });

  return (
    <div
      className="display-flex flex-direction-column mr-1 hideScrollBar"
      style={{
        boxShadow: "4px 0px 24px -2px rgba(0, 92, 188, 0.08)",
        maxWidth: "300px",
        overflowY: "auto",
        margin: "0",
      }}
    >
      {!isPanelOpen && (
        <Icon
          style={{ cursor: "pointer" }}
          onClick={() => updateSidebarPanelState(true)}
          alt="menu"
          className="$spacing-pl05 $spacing-pr04 $spacing-py03"
          icon="menu"
        />
      )}
      <SideBar
        className={`sidebarStyles ${isPanelOpen ? "sidebarStyles-open" : "sidebarStyles-close"}`}
        items={accessibleNavigation}
      />
    </div>
  );
};

export default CustomSideBar;
